import { useSyncExternalStore } from 'react';

/**
 * Can the portal reach its API?
 *  ok      - yes
 *  offline - this computer has no network connection
 *  down    - the network is fine but the ERP server does not answer (restart, deployment, maintenance, outage)
 * A failed API call (after axios-retry) asks for a check; the screen (ServiceStatusScreen) shows while not ok and
 * keeps checking, so the page underneath (and anything typed on it) stays as it was.
 */
export type ServiceState = 'ok' | 'offline' | 'down';

type Snapshot = { state: ServiceState; since: number; checking: boolean; lastCheck: number };

let snap: Snapshot = { state: 'ok', since: Date.now(), checking: false, lastCheck: 0 };
const listeners = new Set<() => void>();
const waiters: Array<() => void> = [];

const set = (patch: Partial<Snapshot>) => {
  const next = { ...snap, ...patch };
  if (patch.state && patch.state !== snap.state) next.since = Date.now();
  snap = next;
  listeners.forEach((l) => l());
  if (snap.state === 'ok') waiters.splice(0).forEach((w) => w());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const useServiceStatus = () => useSyncExternalStore(subscribe, () => snap);
export const serviceState = () => snap.state;

/** run once the API answers again (now, when it already does) */
export const whenServiceOk = (fn: () => void) => {
  if (snap.state === 'ok') fn();
  else waiters.push(fn);
};

const baseUrl = () => String(import.meta.env.APP_API_BASE_URL ?? '').replace(/\/+$/, '') + '/';

/**
 * true when the API itself answers: its ping route ({ ok: true }, no database, no login), or Express's own 404 page
 * from an API build that has no ping yet. A proxy's error (502/503/504, or the 500 a dev proxy gives) or no answer
 * means the server is down.
 */
const reachable = async (): Promise<boolean> => {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 8000);
  try {
    const r = await fetch(baseUrl() + 'ping', { method: 'GET', cache: 'no-store', signal: ctl.signal });
    const text = await r.text();
    if (r.ok) return /"ok"\s*:\s*true/.test(text);
    return r.status === 404 && /Cannot GET/i.test(text);
  } catch {
    return false; // no answer, or a proxy error page without a CORS header
  } finally {
    clearTimeout(timer);
  }
};

let running: Promise<void> | null = null;

/** check the connection now and update the state */
export const checkService = (): Promise<void> => {
  if (running) return running;
  running = (async () => {
    set({ checking: true });
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        set({ state: 'offline' });
        return;
      }
      set({ state: (await reachable()) ? 'ok' : 'down' });
    } finally {
      set({ checking: false, lastCheck: Date.now() });
      running = null;
    }
  })();
  return running;
};

let lastProbe = 0;

/** an API call failed with no answer or a server error: find out whether the server is down (at most every 10 s) */
export const reportApiFailure = () => {
  if (snap.state === 'ok' && Date.now() - lastProbe < 10000) return;
  lastProbe = Date.now();
  void checkService();
};

/** an API call answered: the server is up */
export const reportApiOk = () => {
  if (snap.state !== 'ok') set({ state: 'ok', lastCheck: Date.now() });
};

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => set({ state: 'offline' }));
  window.addEventListener('online', () => {
    if (snap.state !== 'ok') void checkService();
  });
}
